import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { RautFormDataState } from 'pages/reporting/reports/raut/rautReportData'
import { useEffect, useMemo, useState } from 'react'

export type ClientImage = {
  signedUrl: string | undefined
  id: string
  imagePath: string
  imageCaption?: string
  type: string
}

export interface ClientImagesResult {
  images: ClientImage[]
  isLoading: boolean
}

/**
 * Re-encodes an image through a canvas to ensure consistent format for PDF rendering.
 */
async function fetchImageAsBase64(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = img.naturalWidth
        canvas.height = img.naturalHeight

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Failed to get canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0)
        resolve(canvas.toDataURL('image/png', 1.0))
      } catch (error) {
        reject(error)
      }
    }

    img.onerror = () => reject(new Error(`Failed to load image from ${url}`))
    img.src = url
  })
}

export function useClientImages(
  metadataEntryId: string,
  formData: RautFormDataState
): ClientImagesResult {
  const [isConverting, setIsConverting] = useState(true)
  const [imagesWithBase64, setImagesWithBase64] = useState<ClientImage[]>([])

  const { fileRefs: clientImageRefs, isLoading: isLoadingFileRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'client-images/**',
    })

  const { results: clientImageUrls, isLoading: isLoadingUrls } =
    useRolodexSignedURLs(
      clientImageRefs.map((ref) => ({
        entry_id: metadataEntryId,
        path: ref.path,
      }))
    )

  const processedClientImages = useMemo(() => {
    if (!formData.clientProvidedImagesData?.images?.length) return []

    return formData.clientProvidedImagesData.images.map((image) => {
      const signedUrl = clientImageUrls.find(
        (url) => url.path === image.imagePath
      )?.signed_url

      return { ...image, signedUrl }
    })
  }, [formData.clientProvidedImagesData.images, clientImageUrls])

  useEffect(() => {
    if (isLoadingFileRefs || isLoadingUrls) {
      setIsConverting(true)
      return
    }

    const imagesWithUrls = processedClientImages.filter((img) => img.signedUrl)
    if (imagesWithUrls.length === 0) {
      setImagesWithBase64([])
      setIsConverting(false)
      return
    }

    const convertImages = async () => {
      setIsConverting(true)
      try {
        const convertedImages = await Promise.all(
          imagesWithUrls.map(async (img) => {
            try {
              const base64Url = await fetchImageAsBase64(img.signedUrl!)
              return { ...img, signedUrl: base64Url }
            } catch {
              return img
            }
          })
        )
        setImagesWithBase64(convertedImages)
      } catch {
        setImagesWithBase64(imagesWithUrls)
      } finally {
        setIsConverting(false)
      }
    }

    convertImages()
  }, [isLoadingFileRefs, isLoadingUrls, processedClientImages])

  return {
    images: imagesWithBase64,
    isLoading: isConverting || isLoadingFileRefs || isLoadingUrls,
  }
}
